import {
  AVAILABLE_FILTERS_PER_PAGE_COUNT,
  MAX_FILTERS_COUNT,
  MAX_FILTERS_SEARCH_COUNT,
  PATHOLOGY_MAP_SLIDE_ATTRIBUTES_OPTIONS,
  SLIDE_NAME_CHARS_LIMIT,
} from 'components/PathologyMap/constants/common';
import { getNormalizedSlideFileName } from 'utilities/slides';

export const getRefinementLabelByType = (type, categoriesList) => {
  const refinement = categoriesList.find(
    ({ refinementType }) => refinementType === type,
  );

  return refinement?.title ?? type;
};

const getScienceInfoSlideAttributes = (slide) => {
  const scienceInfoSlideAttributes = Object.entries(slide.science_info)
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .map(([key, value]) => ({
      label: key.replace('_', ' '),
      value: typeof value !== 'string' ? value?.toString() : value,
      attribute: key,
    }));

  return scienceInfoSlideAttributes;
};

export const getSlideAttributesFromSlide = ({ slide }) => {
  const slideAttributes = PATHOLOGY_MAP_SLIDE_ATTRIBUTES_OPTIONS.reduce(
    (result, { label, attribute, valueGetter }) => {
      const attributeValue = slide[attribute];
      if (attributeValue) {
        result.push({
          label,
          attribute,
          value: valueGetter ? valueGetter(slide) : attributeValue,
        });
      }

      return result;
    },
    [],
  );

  if (slide.science_info) {
    const scienceInfoSlideAttributes = getScienceInfoSlideAttributes(slide);
    slideAttributes.push(...scienceInfoSlideAttributes);
  }

  return slideAttributes;
};

export const calculateSelectedFiltersSumCount = (selectedFilters) =>
  selectedFilters.reduce(
    (sum, selectedFilter) => sum + selectedFilter.refinements.length,
    0,
  );

export const getFiltersCountText = (filtersCount) => {
  const isPlusSignNeeded =
    filtersCount >= MAX_FILTERS_COUNT ||
    filtersCount === MAX_FILTERS_SEARCH_COUNT;
  const filtersCountText = isPlusSignNeeded ? `${filtersCount}+` : filtersCount;

  return filtersCountText;
};

export const toggleArrayElement = (array, element) => {
  if (array.includes(element)) {
    return array.filter((item) => item !== element);
  }

  return [...array, element];
};

export const getHighlightedWords = (hit, attribute) => {
  const highlightedResult = hit._highlightResult[attribute];
  const highlightedWords = highlightedResult?.matchedWords || [];

  return highlightedWords;
};

export const getNormalizedSlideFileNameTruncate = (slide) => {
  const normalizedSlideName = getNormalizedSlideFileName(slide);
  const truncatedSlideName = normalizedSlideName.slice(
    0,
    SLIDE_NAME_CHARS_LIMIT,
  );

  return truncatedSlideName;
};

export const sortFacetsResults = (facetsResult) =>
  [...facetsResult].sort((a, b) => {
    if (b.count - a.count === 0) {
      return a.value.localeCompare(b.value, undefined, { sensitivity: 'base' });
    }

    return b.count - a.count;
  });

export const getClearedFiltersAttributes = (selectedFilters) => {
  const clearedFilters = selectedFilters.map(({ attribute }) =>
    attribute.replace(/science_info\./gi, ''),
  );

  return clearedFilters;
};

export const convertRefinementsListToFilters = (
  refinements,
  currentRefinement,
) => {
  // ignoring current refinement cause otherwise search will not return expected results
  const filteredRefinements = refinements.filter(
    (refinement) => refinement.attribute !== currentRefinement,
  );

  const filterGroups = filteredRefinements.map((refinementObj) =>
    refinementObj.refinements.map(
      (refinement) => `${refinement.attribute}:"${refinement.value}"`,
    ),
  );

  const filterString = filterGroups
    .map((group) => `(${group.join(' OR ')})`)
    .join(' AND ');

  return filterString;
};

export const getNextPageFacetsResults = (prevResults, allResults) => {
  const newResults = allResults.slice(
    prevResults.length,
    prevResults.length + AVAILABLE_FILTERS_PER_PAGE_COUNT,
  );

  return [...prevResults, ...newResults];
};

export const getSlideCardIndexResult = (idx, allResultsCount) => {
  const result = `${idx + 1}/${allResultsCount}`;

  return result;
};

export const findFavoriteCategory = (refinementType, favoriteCategories) =>
  favoriteCategories.find(
    (favCategory) => favCategory.refinementType === refinementType,
  );

export const hideNonFavoriteCategoriesAndSort = (
  allCategories,
  favoriteCategories,
) =>
  allCategories
    .map((category) => {
      const favCategory = findFavoriteCategory(
        category.refinementType,
        favoriteCategories,
      );

      return {
        ...category,
        isGroupEnd: true,
        createdAt: favCategory?.createdAt || null,
        isHidden: !favCategory,
      };
    })
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

export const hideInvisibleCategories = (allCategories, presetCategories) =>
  allCategories.map((category) =>
    presetCategories.find(
      (presetCategory) =>
        presetCategory.refinementType === category.refinementType,
    )
      ? category
      : { isHidden: true, ...category },
  );

export const getAttributesFromSlide = ({ slide }) => {
  const slideAttributes = [];

  if (slide.name) {
    let modifiedName = slide.name.replace(/\$/g, '_');
    const orderID = slide.order_id;

    modifiedName = modifiedName
      .replace(`${orderID}_`, '')
      .replace('.svs', '')
      .replace('.scn', '');

    slideAttributes.push({
      label: 'Name',
      value: modifiedName,
    });
  }
  if (slide.antibody_name) {
    slideAttributes.push({
      label: 'Antibody',
      value: slide.antibody_name,
    });
  }
  if (slide.control_type_name) {
    slideAttributes.push({
      label: 'Control Type',
      value: slide.control_type_name,
    });
  }
  if (slide.cut_type) {
    slideAttributes.push({ label: 'Cut Type', value: slide.cut_type });
  }
  if (slide.fixation_time_hours) {
    slideAttributes.push({
      label: 'Fixation Time',
      value: slide.fixation_time_hours,
    });
  }
  if (slide.fixative_name) {
    slideAttributes.push({
      label: 'Fixative',
      value: slide.fixative_name,
    });
  }
  if (slide.height) {
    slideAttributes.push({ label: 'Height', value: slide.height });
  }

  if (slide.order_name) {
    slideAttributes.push({ label: 'Order', value: slide.order_name });
  }
  if (slide.order_external_notes) {
    slideAttributes.push({
      label: 'Order Notes',
      value: slide.order_external_notes,
    });
  }
  if (slide.organ_name) {
    slideAttributes.push({ label: 'Organ', value: slide.organ_name });
  }
  if (slide.sample_name) {
    slideAttributes.push({ label: 'Sample', value: slide.sample_name });
  }
  if (slide.special_stain_name) {
    slideAttributes.push({
      label: 'Special Stain Name',
      value: slide.special_stain_name,
    });
  }
  if (slide.species_name) {
    slideAttributes.push({ label: 'Species', value: slide.species_name });
  }
  if (slide.stain_type_name) {
    slideAttributes.push({ label: 'Stain Type', value: slide.stain_type_name });
  }
  if (slide.submission_type_name) {
    slideAttributes.push({
      label: 'Submission Type',
      value: slide.submission_type_name,
    });
  }

  if (slide.science_info) {
    const scienceInfoKeys = Object.keys(slide.science_info);
    scienceInfoKeys.sort();

    scienceInfoKeys.forEach((key) => {
      let serialized = slide.science_info[key];
      if (typeof serialized !== 'string') serialized = serialized.toString();
      slideAttributes.push({
        label: key.replace('_', ' '),
        value: serialized,
      });
    });
  }

  return slideAttributes;
};
